import React from 'react';
import Alpha, { BACKGROUND_IMG } from '../color-alpha';
import Hue from '../color-hue';
import Saturation from '../color-saturation';
import {
    ColorResult,
    color as handleColor,
    hexToHsva,
    HsvaColor,
    hsvaToHex,
    hsvaToRgbaString,
    validHex,
} from '../utils/color-convert';

export interface ColorfulPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'color'> {
    prefixCls?: string;
    onChange?: (color: ColorResult) => void;
    color?: string | HsvaColor;
    disableAlpha?: boolean;
}

const Pointer = ({ style, color, ...props }: React.HTMLAttributes<HTMLDivElement> & { color: string }) => (
    <div
        {...props}
        style={{
            height: 28,
            width: 28,
            position: 'absolute',
            transform: 'translate(-14px, -4px)',
            boxShadow: '0 2px 4px rgb(0 0 0 / 20%)',
            borderRadius: '50%',
            background: `url(${BACKGROUND_IMG})`,
            backgroundColor: '#fff',
            border: '2px solid #fff',
            zIndex: 1,
            ...style,
        }}
    >
        <div
            style={{
                backgroundColor: color,
                borderRadius: '50%',
                height: ' 100%',
                width: '100%',
            }}
        />
    </div>
);

const ColorfulPicker = React.forwardRef<HTMLDivElement, ColorfulPickerProps>((props, ref) => {
    const { prefixCls = 'c-color-colorful-picker', className, onChange, color, style, disableAlpha, ...other } = props;
    const hsva = (typeof color === 'string' && validHex(color) ? hexToHsva(color) : color || {}) as HsvaColor;
    const handleChange = (value: HsvaColor) => onChange && onChange(handleColor(value));
    return (
        <div
            ref={ref}
            style={{
                width: 200,
                position: 'relative',
                ...style,
            }}
            {...other}
            className={`${prefixCls} ${className || ''}`}
        >
            <Saturation
                hsva={hsva}
                className={prefixCls}
                radius="8px 8px 0 0"
                style={{ width: 'auto', height: 150, minWidth: 120, borderBottom: '12px solid #000' }}
                pointer={({ left, top, color }) => (
                    <Pointer style={{ left, top, transform: 'translate(-16px, -16px)' }} color={hsvaToHex(hsva)} />
                )}
                onChange={(newColor) => handleChange({ ...hsva, ...newColor })}
            />
            <Hue
                hsva={hsva}
                height={24}
                radius={disableAlpha ? '0 0 8px 8px' : 0}
                className={prefixCls}
                onChange={(newHue) => handleChange({ ...hsva, ...newHue })}
                pointer={({ left }) => <Pointer style={{ left }} color={`hsl(${hsva.h || 0}deg 100% 50%)`} />}
            />
            {!disableAlpha && (
                <Alpha
                    hsva={hsva}
                    height={24}
                    className={prefixCls}
                    radius="0 0 8px 8px"
                    pointer={({ left }) => <Pointer style={{ left }} color={hsvaToRgbaString(hsva)} />}
                    onChange={(newAlpha) => handleChange({ ...hsva, ...newAlpha })}
                />
            )}
        </div>
    );
});

ColorfulPicker.displayName = 'ColorfulPicker';

export default ColorfulPicker;
